import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FirebaseStorage, getDownloadURL, getStorage, ref } from 'firebase/storage';
import { Subscription } from 'rxjs';

export interface PresentationSlide {
  id: string;
  text: string;
  imagePath?: string;
  imageUrl?: string | URL;
  breadcrumb: string[];
}

export interface NotebookPresentationDialogData {
  slides: PresentationSlide[];
  initialIndex: number;
}

type ImageState = 'loading' | 'loaded' | 'error';
type PresentationImage = { slideId: string; url: string };
type BreadcrumbItem = { label: string; kind: 'notebook' | 'ellipsis' | 'parent' | 'current' };

@Component({
  selector: 'app-notebook-presentation-dialog',
  templateUrl: './notebook-presentation-dialog.component.html',
  styleUrls: ['./notebook-presentation-dialog.component.scss']
})
export class NotebookPresentationDialogComponent implements AfterViewInit, OnDestroy {
  readonly slides: PresentationSlide[];
  currentIndex: number;
  textSizePx = 32;
  breadcrumbItems: BreadcrumbItem[] = [];

  private readonly storage: FirebaseStorage;
  private readonly imageUrls = new Map<string, string>();
  private readonly imageStates = new Map<string, ImageState>();
  private readonly pendingImages = new Map<string, Promise<void>>();
  private readonly preloadedImages = new Map<string, HTMLImageElement>();
  private readonly keydownSubscription: Subscription;
  private readonly openedSubscription: Subscription;
  private resizeObserver?: ResizeObserver;
  private textViewport?: ElementRef<HTMLElement>;
  private textElement?: ElementRef<HTMLElement>;
  private fitFrame?: number;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;

  @ViewChild('textViewport')
  set textViewportRef(element: ElementRef<HTMLElement> | undefined) {
    this.resizeObserver?.disconnect();
    this.textViewport = element;

    if (element && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver ??= new ResizeObserver(() => this.scheduleTextFit());
      this.resizeObserver.observe(element.nativeElement);
    }

    this.scheduleTextFit();
  }

  @ViewChild('slideText')
  set slideTextRef(element: ElementRef<HTMLElement> | undefined) {
    this.textElement = element;
    this.scheduleTextFit();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) data: NotebookPresentationDialogData,
    private readonly dialogRef: MatDialogRef<NotebookPresentationDialogComponent>,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.slides = data?.slides ?? [];
    this.currentIndex = Math.min(Math.max(data?.initialIndex ?? 0, 0), Math.max(this.slides.length - 1, 0));
    this.storage = getStorage();
    this.updateBreadcrumbItems();

    this.keydownSubscription = this.dialogRef.keydownEvents().subscribe(event => {
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    });

    this.openedSubscription = this.dialogRef.afterOpened().subscribe(() => this.scheduleTextFit());
    this.prepareNearbyImages();
  }

  ngAfterViewInit(): void {
    this.scheduleTextFit();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateBreadcrumbItems();
    this.scheduleTextFit();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.keydownSubscription.unsubscribe();
    this.openedSubscription.unsubscribe();
    this.resizeObserver?.disconnect();
    for (const image of this.preloadedImages.values()) {
      if (!image.complete) {
        image.removeAttribute('src');
      }
    }
    this.preloadedImages.clear();

    if (this.fitFrame !== undefined) {
      cancelAnimationFrame(this.fitFrame);
    }

    if (this.retryTimer !== undefined) {
      clearTimeout(this.retryTimer);
    }
  }

  get currentSlide(): PresentationSlide | undefined {
    return this.slides[this.currentIndex];
  }

  get hasText(): boolean {
    return Boolean(this.currentSlide?.text?.trim());
  }

  get hasImage(): boolean {
    return Boolean(this.currentSlide?.imageUrl || this.currentSlide?.imagePath);
  }

  get currentImageUrl(): string | undefined {
    const slide = this.currentSlide;
    return slide ? this.imageUrls.get(slide.id) : undefined;
  }

  get currentImages(): PresentationImage[] {
    const slide = this.currentSlide;
    const url = this.currentImageUrl;
    return slide && url && this.currentImageState !== 'error'
      ? [{ slideId: slide.id, url }]
      : [];
  }

  get currentImageState(): ImageState | undefined {
    const slide = this.currentSlide;
    return slide ? this.imageStates.get(slide.id) : undefined;
  }

  get breadcrumbLabel(): string {
    return this.currentSlide?.breadcrumb?.join(' > ') ?? '';
  }

  get slideAnnouncement(): string {
    return this.slides.length
      ? `Note ${this.currentIndex + 1} of ${this.slides.length}. ${this.breadcrumbLabel || 'Untitled note'}`
      : 'No notes to present';
  }

  previous(): void {
    this.goTo(this.currentIndex - 1);
  }

  next(): void {
    this.goTo(this.currentIndex + 1);
  }

  close(): void {
    this.dialogRef.close();
  }

  trackImage(_index: number, image: PresentationImage): string {
    return image.slideId;
  }

  onImageLoaded(slideId: string): void {
    if (this.currentSlide?.id === slideId) {
      this.imageStates.set(slideId, 'loaded');
    }
  }

  onImageError(slideId: string): void {
    if (this.currentSlide?.id === slideId) {
      this.imageStates.set(slideId, 'error');
    }
  }

  retryImage(): void {
    const slide = this.currentSlide;
    if (!slide) {
      return;
    }

    this.imageUrls.delete(slide.id);
    this.imageStates.set(slide.id, 'loading');
    const preloadedImage = this.preloadedImages.get(slide.id);
    if (preloadedImage && !preloadedImage.complete) {
      preloadedImage.removeAttribute('src');
    }
    this.preloadedImages.delete(slide.id);
    // A new render removes the failed <img> before resolving the same URL again.
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      this.ensureImageUrl(slide);
    }, 0);
  }

  private goTo(index: number): void {
    if (index < 0 || index >= this.slides.length || index === this.currentIndex) {
      return;
    }

    this.currentIndex = index;
    this.textSizePx = 32;
    const currentSlide = this.currentSlide;
    if (currentSlide?.imageUrl || currentSlide?.imagePath) {
      this.imageStates.set(currentSlide.id, 'loading');
    }
    this.updateBreadcrumbItems();
    this.prepareNearbyImages();
    this.scheduleTextFit();
  }

  private updateBreadcrumbItems(): void {
    const path = this.currentSlide?.breadcrumb ?? [];
    if (!path.length) {
      this.breadcrumbItems = [];
      return;
    }

    const current: BreadcrumbItem = { label: path[path.length - 1], kind: 'current' };
    if (path.length === 1) {
      this.breadcrumbItems = [current];
      return;
    }

    const ellipsis: BreadcrumbItem = { label: '…', kind: 'ellipsis' };
    const width = typeof window === 'undefined' ? 1024 : window.innerWidth;
    if (width <= 420) {
      this.breadcrumbItems = [ellipsis, current];
      return;
    }

    const notebook: BreadcrumbItem = { label: path[0], kind: 'notebook' };
    if (width <= 700) {
      this.breadcrumbItems = path.length > 2
        ? [notebook, ellipsis, current]
        : [notebook, current];
      return;
    }

    if (path.length > 3) {
      this.breadcrumbItems = [
        notebook,
        ellipsis,
        { label: path[path.length - 2], kind: 'parent' },
        current
      ];
    } else if (path.length === 3) {
      this.breadcrumbItems = [notebook, { label: path[1], kind: 'parent' }, current];
    } else {
      this.breadcrumbItems = [notebook, current];
    }
  }

  private prepareNearbyImages(): void {
    const nearbySlides = [this.currentIndex, this.currentIndex + 1, this.currentIndex - 1]
      .filter(index => index >= 0 && index < this.slides.length)
      .map(index => this.slides[index]);
    const nearbyIds = new Set(nearbySlides.map(slide => slide.id));

    for (const [slideId, image] of this.preloadedImages) {
      if (!nearbyIds.has(slideId)) {
        if (!image.complete) {
          image.removeAttribute('src');
        }
        this.preloadedImages.delete(slideId);
      }
    }

    nearbySlides.forEach(slide => this.ensureImageUrl(slide));
  }

  private ensureImageUrl(slide: PresentationSlide): void {
    if (!slide.imageUrl && !slide.imagePath) {
      return;
    }

    const cachedUrl = this.imageUrls.get(slide.id);
    if (cachedUrl) {
      this.preloadImage(slide, cachedUrl);
      return;
    }

    if (this.pendingImages.has(slide.id)) {
      return;
    }

    this.imageStates.set(slide.id, 'loading');

    if (slide.imageUrl) {
      const url = String(slide.imageUrl);
      this.imageUrls.set(slide.id, url);
      this.preloadImage(slide, url);
      return;
    }

    const load = getDownloadURL(ref(this.storage, slide.imagePath!))
      .then(url => {
        if (this.destroyed) {
          return;
        }
        this.imageUrls.set(slide.id, url);
        this.preloadImage(slide, url);
        this.changeDetector.markForCheck();
      })
      .catch(() => {
        if (this.destroyed) {
          return;
        }
        this.imageStates.set(slide.id, 'error');
        this.changeDetector.markForCheck();
      })
      .finally(() => this.pendingImages.delete(slide.id));

    this.pendingImages.set(slide.id, load);
  }

  private preloadImage(slide: PresentationSlide, url: string): void {
    if (this.destroyed || typeof Image === 'undefined' || this.preloadedImages.has(slide.id)) {
      return;
    }

    const nearby = [this.currentIndex - 1, this.currentIndex, this.currentIndex + 1]
      .some(index => this.slides[index]?.id === slide.id);
    if (!nearby) {
      return;
    }

    // A detached Image downloads the file now; decode warms it for the visible slide.
    const image = new Image();
    image.decoding = 'async';
    this.preloadedImages.set(slide.id, image);
    image.src = url;
    if (typeof image.decode === 'function') {
      void image.decode().catch(() => { /* The visible image handles load errors. */ });
    }
  }

  private scheduleTextFit(): void {
    if (this.fitFrame !== undefined) {
      cancelAnimationFrame(this.fitFrame);
    }

    this.fitFrame = requestAnimationFrame(() => {
      this.fitFrame = undefined;
      this.fitText();
    });
  }

  private fitText(): void {
    const viewport = this.textViewport?.nativeElement;
    const text = this.textElement?.nativeElement;
    if (!viewport || !text || !this.hasText || viewport.clientHeight < 1 || viewport.clientWidth < 1) {
      return;
    }

    const minimum = this.hasImage ? 16 : 24;
    let low = minimum;
    let high = this.hasImage ? 48 : 76;
    let best = minimum;

    while (low <= high) {
      const size = Math.floor((low + high) / 2);
      text.style.fontSize = `${size}px`;

      if (text.scrollHeight <= viewport.clientHeight - 2 && text.scrollWidth <= viewport.clientWidth + 2) {
        best = size;
        low = size + 1;
      } else {
        high = size - 1;
      }
    }

    text.style.fontSize = `${best}px`;
    this.textSizePx = best;
    this.changeDetector.markForCheck();
  }
}
